import React from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip
} from '@mui/material';
import { 
  ExpandMore as ExpandMoreIcon,
  Schema as SchemaIcon,
  Code as CodeIcon,
  Output as OutputIcon,
  Chat as ChatIcon,
  DataObject as DataObjectIcon,
  Functions as FunctionsIcon
} from '@mui/icons-material';
import { FunctionCall, AgentResult } from '../../services/playlistAgent';
import { ChatMessage } from '../../services/openaiService';
import PlaylistList from './PlaylistList';
import PlaylistDetails from './PlaylistDetails';
import GeneratedPlaylist from './GeneratedPlaylist';
import { playlistAgentTools } from '../../services/playlistAgentSchema';

interface FunctionCallSequenceProps {
  results: AgentResult[];
  functionCalls: FunctionCall[];
  onViewDetails: (playlistId: string) => void;
  onCreateInSpotify: (playlistData: any) => void;
  conversationHistory?: ChatMessage[]; // Add this prop
}

const FunctionCallSequence: React.FC<FunctionCallSequenceProps> = ({ 
  results, 
  functionCalls,
  onViewDetails,
  onCreateInSpotify,
  conversationHistory
}) => {
  // Render a specific result based on its type
  const renderResult = (result: AgentResult, index: number) => {
    switch (result.type) {
      case 'playlists':
        return (
          <PlaylistList 
            playlists={result.data} 
            onViewDetails={onViewDetails} 
          />
        );
        
      case 'playlistDetails':
        return (
          <PlaylistDetails playlist={result.data} />
        );
        
      case 'playlistGenerated':
        return (
          <GeneratedPlaylist 
            playlist={result.data} 
            onCreateInSpotify={onCreateInSpotify} 
          />
        );
        
      default:
        return (
          <Typography>Result type: {result.type}</Typography>
        );
    }
  };

  return (
    <Paper sx={{ p: 2, mb: 3, bgcolor: 'background.default', borderRadius: 2 }}>
      <Typography variant="h6" gutterBottom color="primary.main">
        Function Call Sequence
      </Typography>
      
      <Typography variant="body2" paragraph color="text.secondary">
        The agent executed multiple functions to complete your request:
      </Typography>
      
      <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {functionCalls.map((call, index) => (
          <Chip 
            key={index}
            label={call.function}
            color="primary"
            variant="outlined"
            size="small"
          />
        ))}
      </Box>
      
      <Accordion defaultExpanded={true}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <FunctionsIcon sx={{ mr: 1, fontSize: 20, color: 'primary.main' }} />
            <Typography variant="h6">Function Calls</Typography>
            <Chip 
              label={`${functionCalls.length} functions`} 
              size="small" 
              color="primary" 
              variant="outlined" 
              sx={{ ml: 2 }} 
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Stepper orientation="vertical">
            {functionCalls.map((call, index) => (
              <Step key={index} active={true} completed={true}>
                <StepLabel>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="subtitle2" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                      {call.function}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      {playlistAgentTools.find(tool => tool.function.name === call.function)?.function.description}
                    </Typography>
                  </Box>
                </StepLabel>
                <StepContent>
              <Box sx={{ mb: 2 }}>
                {/* Function Schema */}
                <Accordion sx={{ mb: 2, bgcolor: 'background.paper' }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <SchemaIcon sx={{ mr: 1, fontSize: 18, color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary">
                        Function Schema
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box 
                      sx={{ 
                        p: 1, 
                        bgcolor: 'background.default', 
                        borderRadius: 1, 
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                        overflowX: 'auto',
                        border: '1px solid',
                        borderColor: 'divider'
                      }}
                    >
                      <pre style={{ margin: 0 }}>
                        {JSON.stringify(
                          playlistAgentTools.find(
                            tool => tool.function.name === call.function
                          )?.function || { name: call.function, description: "Function schema not found" },
                          null, 2
                        )}
                      </pre>
                    </Box>
                  </AccordionDetails>
                </Accordion>
                
                {/* Parameters */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CodeIcon sx={{ mr: 1, fontSize: 18, color: 'primary.main' }} />
                  <Typography variant="caption" color="text.secondary">
                    Parameters:
                  </Typography>
                </Box>
                <Box 
                  sx={{ 
                    p: 1, 
                    bgcolor: 'background.paper', 
                    borderRadius: 1, 
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                    mb: 2,
                    border: '1px solid',
                    borderColor: 'divider'
                  }}
                >
                  <pre style={{ margin: 0, overflow: 'auto' }}>
                    {JSON.stringify(call.parameters, null, 2)}
                  </pre>
                </Box>
                
                {/* Result */}
                {index < results.length && (
                  <Box sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <OutputIcon sx={{ mr: 1, fontSize: 18, color: 'success.main' }} />
                      <Typography variant="caption" color="text.secondary">
                        Result:
                      </Typography>
                    </Box>
                    <Box sx={{ mt: 1, border: '1px solid', borderColor: 'success.light', borderRadius: 1, p: 1 }}>
                      {renderResult(results[index], index)}
                    </Box>
                  </Box>
                )}
                  </Box>
                </StepContent>
              </Step>
            ))}
          </Stepper>
        </AccordionDetails>
      </Accordion>

      {/* Conversation History - Collapsible */}
      {conversationHistory && conversationHistory.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Accordion defaultExpanded={false}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ChatIcon sx={{ mr: 1, fontSize: 20, color: 'primary.main' }} />
                <Typography variant="h6">Conversation Flow</Typography>
                <Chip 
                  label={`${conversationHistory.length} messages`} 
                  size="small" 
                  color="primary" 
                  variant="outlined" 
                  sx={{ ml: 2 }} 
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Paper sx={{ p: 2 }}>
                {conversationHistory.map((message, index) => (
                  <Box key={index} sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="primary">
                      {message.role === 'user' ? 'User' : 
                      message.role === 'assistant' ? 'Assistant' : 
                      message.role === 'tool' ? 'Tool Result' : 'System'}:
                    </Typography>
                    
                    {/* Make message content collapsible if it's a tool result or assistant with JSON */}
                    {(message.role === 'tool' || message.role === 'assistant') && 
                     message.content && 
                     (message.role === 'tool' || message.content.includes('{')) ? (
                      <Accordion 
                        sx={{ 
                          bgcolor: (
                            message.role === 'assistant' ? 'secondary.light' : 
                            message.role === 'tool' ? 'success.light' : 'grey.100'
                          ),
                          '&:before': { display: 'none' }, // Remove the default divider
                          boxShadow: 'none'
                        }}
                      >
                        <AccordionSummary 
                          expandIcon={<ExpandMoreIcon />}
                          sx={{ minHeight: '36px', p: 0.5 }}
                        >
                          <Typography variant="caption">
                            {message.role === 'tool' ? 'View Tool Result' : 'View Response'}
                            {message.content && ` (${message.content.length} chars)`}
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 1 }}>
                          {/* Try to parse as JSON for better display */}
                          {(() => {
                            try {
                              // Check if the content is JSON
                              if (message.content) {
                                const jsonContent = JSON.parse(message.content);
                                return (
                                  <Box 
                                    sx={{ 
                                      fontFamily: 'monospace',
                                      fontSize: '0.875rem',
                                      overflowX: 'auto'
                                    }}
                                  >
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                      <DataObjectIcon sx={{ mr: 1, color: 'info.main', fontSize: 16 }} />
                                      <Typography variant="caption" color="info.main">
                                        JSON Data
                                      </Typography>
                                    </Box>
                                    <pre style={{ margin: 0 }}>
                                      {JSON.stringify(jsonContent, null, 2)}
                                    </pre>
                                  </Box>
                                );
                              }
                              return null;
                            } catch (e) {
                              // If not JSON, display as plain text
                              return (
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                  {message.content}
                                </Typography>
                              );
                            }
                          })()}
                        </AccordionDetails>
                      </Accordion>
                    ) : (
                      // Regular display for user messages and simple text
                      <Typography variant="body2" sx={{ 
                        p: 1, 
                        bgcolor: message.role === 'user' ? 'primary.light' : 
                                message.role === 'assistant' ? 'secondary.light' : 
                                message.role === 'tool' ? 'success.light' : 
                                message.role === 'system' ? 'grey.100' : 'grey.100',
                        borderRadius: 1,
                        whiteSpace: 'pre-wrap'
                      }}>
                        {message.content}
                      </Typography>
                    )}
                    
                    {/* Display tool calls if present - collapsible */}
                    {message.tool_calls && message.tool_calls.length > 0 && (
                      <Box sx={{ mt: 1, ml: 2 }}>
                        <Accordion sx={{ boxShadow: 'none', bgcolor: 'transparent' }}>
                          <AccordionSummary 
                            expandIcon={<ExpandMoreIcon />}
                            sx={{ minHeight: '36px', p: 0.5 }}
                          >
                            <Typography variant="caption" color="text.secondary">
                              Tool Calls ({message.tool_calls.length})
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails sx={{ p: 0 }}>
                            {message.tool_calls.map((toolCall: any, idx: number) => (
                              <Box key={idx} sx={{ 
                                mt: 1, 
                                p: 1, 
                                bgcolor: 'background.paper',
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'divider'
                              }}>
                                <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                  {toolCall.function.name}
                                </Typography>
                                <Box 
                                  sx={{ 
                                    mt: 0.5,
                                    p: 1, 
                                    bgcolor: 'background.default',
                                    borderRadius: 1,
                                    fontFamily: 'monospace',
                                    fontSize: '0.75rem'
                                  }}
                                >
                                  <pre style={{ margin: 0 }}>
                                    {JSON.stringify(JSON.parse(toolCall.function.arguments), null, 2)}
                                  </pre>
                                </Box>
                              </Box>
                            ))}
                          </AccordionDetails>
                        </Accordion>
                      </Box>
                    )}
                  </Box>
                ))}
              </Paper>
            </AccordionDetails>
          </Accordion>
        </Box>
      )}
    </Paper>
  );
};

export default FunctionCallSequence;
